import type { LeadStatus } from '../types';

interface StatusBadgeProps {
  status: LeadStatus;
  size?: 'sm' | 'md' | 'lg';
}

export function StatusBadge({ status, size = 'md' }: StatusBadgeProps) {
  const statusConfig: Record<LeadStatus, { label: string; bgClass: string; textClass: string }> = {
    new: {
      label: 'New',
      bgClass: 'bg-blue-100',
      textClass: 'text-blue-800',
    },
    qualified: {
      label: 'Qualified',
      bgClass: 'bg-green-100',
      textClass: 'text-green-800',
    },
    disqualified: {
      label: 'Disqualified',
      bgClass: 'bg-red-100',
      textClass: 'text-red-800',
    },
  };

  const config = statusConfig[status];
  
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base',
  };

  return (
    <span 
      className={`inline-flex items-center font-medium rounded-full ${config.bgClass} ${config.textClass} ${sizeClasses[size]}`}
    >
      {config.label}
    </span>
  );
}

// Lead score badge (1-10 scale)
interface LeadScoreBadgeProps {
  score: number;
}

export function LeadScoreBadge({ score }: LeadScoreBadgeProps) {
  const getColorClass = () => {
    if (score >= 8) return 'bg-green-500';
    if (score >= 5) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="flex items-center space-x-1">
      <div className="flex">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <div
            key={i}
            className={`w-1.5 h-4 ${i <= score ? getColorClass() : 'bg-gray-200'} ${i === 1 ? 'rounded-l-sm' : ''} ${i === 10 ? 'rounded-r-sm' : ''}`}
          />
        ))}
      </div>
      <span className="text-xs text-gray-600 font-medium ml-1">{score}/10</span>
    </div>
  );
}

// Motivation score badge (1-10 scale)
interface MotivationBadgeProps {
  score: number;
  label?: string;
}

export function MotivationBadge({ score, label = 'Motivation' }: MotivationBadgeProps) {
  const getColorClass = () => {
    if (score >= 8) return 'text-green-600';
    if (score >= 5) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="flex items-center space-x-2">
      <span className="text-xs text-gray-500">{label}:</span>
      <span className={`text-sm font-semibold ${getColorClass()}`}>{score}/10</span>
    </div>
  );
}
